'use client'

import { useTranslation } from 'react-i18next'

import { StatusSuccessIcon } from '@dfds-frontend/navigator-icons/icons'

import { useBasket } from '@/providers/basket-context'

import Price from '../shared/Price'

type Props = {
  basket?: ReturnType<typeof useBasket>['basket']
  /** The sticky mobile button has no room for the original price and subsidy rows. */
  hideSubsidyBreakdown?: boolean
}

export const TotalPriceBlock = ({ basket: suppliedBasket, hideSubsidyBreakdown = false }: Props) => {
  const { basket: contextBasket } = useBasket()
  const basket = suppliedBasket ?? contextBasket
  const { t } = useTranslation()

  const totalPrice = basket?.priceStructure?.totalPrice || 0
  const totalOfferPrice = (basket?.priceStructure?.offerAmount ?? 0) < 0 ? basket?.priceStructure?.totalPriceWithoutOffer : 0
  const currency = basket?.priceStructure?.currency
  const hidePrice = basket?.departures[0]?.offer?.hidePriceAdjustment

  const originalPrice = basket?.priceStructure?.originalPrice || 0
  const subsidies = basket?.priceStructure?.subsidyAmount || 0

  // Dummy departures have no id, so the discount only applies once a real sailing is picked.
  const hasResidentVehicleSubsidy = basket?.commercialDiscount === 'Resident' && basket.departures.some((d) => d.id)

  return (
    <div className="flex flex-col">
      {hasResidentVehicleSubsidy ? (
        <div className="mb-semantic-global-md gap-semantic-global-xs flex flex-col bg-white">
          {!hideSubsidyBreakdown && (subsidies || totalOfferPrice) ? (
            <>
              <div className="flex flex-row items-center justify-between">
                <span className="typography-body-md-medium text-semantic-global-text-light" data-e2e="booking-summary-original-price">
                  {t('ORIGINAL-PRICE')}
                </span>
                <span className="typography-body-sm-regular text-semantic-global-text-light line-through">
                  <Price currency={undefined} price={originalPrice} />
                </span>
              </div>
              {subsidies ? (
                <div className="flex flex-row items-center justify-between">
                  <span className="typography-body-md-medium text-semantic-global-text-light" data-e2e="booking-summary-subsidies">
                    {t('SUBSIDIES')}
                  </span>
                  <span className="typography-body-sm-regular text-semantic-global-text-light">
                    <Price currency={undefined} price={-subsidies} />
                  </span>
                </div>
              ) : null}
            </>
          ) : null}
          <div className="text-semantic-global-success-default flex flex-row items-center">
            <div className="typography-body-md-medium text-left" data-e2e="resident-vehicle-discount-applied-display-text">
              {t('RESIDENT-VEHICLE-DISCOUNT-APPLIED')}
            </div>
            <div className="ml-auto">
              <StatusSuccessIcon size="md" />
            </div>
          </div>
        </div>
      ) : null}
      {totalOfferPrice && !hidePrice && !hasResidentVehicleSubsidy ? (
        <div className="flex flex-row items-center justify-between">
          <span className="typography-body-md-medium text-semantic-global-text-light" data-e2e="booking-summary-original-price">
            {t('ORIGINAL-PRICE')}
          </span>
          <span
            className="typography-body-sm-regular text-semantic-global-text-light line-through"
            data-e2e="booking-summary-total-offer-price"
          >
            <Price currency={undefined} price={totalOfferPrice} />
          </span>
        </div>
      ) : null}
      <div className="flex flex-row items-end justify-between">
        <div className="typography-body-md-medium" data-e2e="booking-summary-total-price">
          {t('TOTAL-PRICE')}
        </div>
        <div
          className="typography-number-sm text-semantic-global-text-default"
          data-e2e="booking-summary-total-price"
          data-e2e-total-price={totalPrice}
          data-e2e-total-price-mobile={totalPrice}
        >
          <Price currency={currency} price={totalPrice} />
        </div>
      </div>
    </div>
  )
}
